// ./src/utils/icalParser.ts
import * as ical from "ical";
import { ParsedEvent } from "@/components/features";

/**
 * iCal parser utility using the 'ical' library
 * Install with: npm install ical @types/ical
 */

export class ICalParseError extends Error {
  constructor(message: string, public originalError?: any) {
    super(message);
    this.name = "ICalParseError";
  }
}

/**
 * Parse iCal file content and extract events
 */
export function parseICalFile(content: string): ParsedEvent[] {
  try {
    // Validate basic iCal structure
    if (!validateICalContent(content)) {
      throw new ICalParseError("Invalid iCal file format");
    }
    // Parse using ical library
    const parsed = ical.parseICS(content);
    console.log("2. Parsed", parsed);
    const events: ParsedEvent[] = [];

    // Convert parsed events to our format
    Object.values(parsed).forEach((item, index) => {
      if (item.type === "VEVENT") {
        try {
          const parsedEvent = convertICalEventToParsedEvent(item, index);
          events.push(parsedEvent);
        } catch (error) {
          console.warn("Failed to parse event:", item, error);
          // Continue parsing other events even if one fails
        }
      }
    });

    return events;
  } catch (error) {
    if (error instanceof ICalParseError) {
      throw error;
    }
    throw new ICalParseError(
      `Failed to parse iCal file: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      error
    );
  }
}

/**
 * Convert ical library event object to our ParsedEvent format
 */
function convertICalEventToParsedEvent(
  icalEvent: any,
  index: number
): ParsedEvent {
  // Generate a temporary ID
  const tempId = icalEvent.uid || `temp-event-${index}-${Date.now()}`;

  // Extract dates
  const startDateTime = icalEvent.start
    ? icalEvent.start.toISOString()
    : undefined;
  const endDateTime = icalEvent.end ? icalEvent.end.toISOString() : undefined;

  // Determine if it's an all-day event
  // ical library sets datetype to 'date' for all-day events
  const isAllDay =
    icalEvent.datetype === "date" ||
    (icalEvent.start && !hasTimeComponent(icalEvent.start));

  // Parse attendees
  const attendees = parseAttendees(icalEvent.attendee);

  // Parse organizer
  const organizer = parseOrganizer(icalEvent.organizer);

  // Parse status
  const status = parseStatus(icalEvent.status);

  return {
    tempId,
    title: icalEvent.summary || "Untitled Event",
    description: icalEvent.description || undefined,
    startDateTime,
    endDateTime,
    location: icalEvent.location || undefined,
    isAllDay,
    organizer,
    attendees,
    status,
    uid: icalEvent.uid,
    transparency:
      icalEvent.transp?.toLowerCase() === "transparent" ? "free" : "busy",
    recurrenceRule: icalEvent.rrule
      ? formatRecurrenceRule(icalEvent.rrule)
      : "",
    originalData: icalEvent, // Store original for debugging
  };
}

/**
 * Check if a date has time component
 */
function hasTimeComponent(date: Date): boolean {
  return (
    date.getHours() !== 0 || date.getMinutes() !== 0 || date.getSeconds() !== 0
  );
}

/**
 * Parse attendees from ical event
 */
function parseAttendees(attendeeData: any): string[] {
  if (!attendeeData) return [];

  // Handle different formats that ical library might return
  if (Array.isArray(attendeeData)) {
    return attendeeData.map(extractEmailFromAttendee).filter(Boolean);
  }

  if (typeof attendeeData === "object") {
    // Single attendee object
    const email = extractEmailFromAttendee(attendeeData);
    return email ? [email] : [];
  }

  if (typeof attendeeData === "string") {
    // String format
    const email = extractEmailFromString(attendeeData);
    return email ? [email] : [];
  }

  return [];
}

/**
 * Extract email from attendee object or string
 */
function extractEmailFromAttendee(attendee: any): string {
  if (!attendee) return "";

  // If attendee is an object with params
  if (typeof attendee === "object") {
    // Check for direct email property
    if (attendee.val && typeof attendee.val === "string") {
      return extractEmailFromString(attendee.val);
    }

    // Check params for CN (common name) or email
    if (attendee.params && attendee.params.CN) {
      return attendee.params.CN;
    }
  }

  // If attendee is a string
  if (typeof attendee === "string") {
    return extractEmailFromString(attendee);
  }

  return "";
}

/**
 * Extract email from mailto: string or return as-is
 */
function extractEmailFromString(str: string): string {
  if (!str) return "";

  // Remove mailto: prefix if present
  if (str.toLowerCase().startsWith("mailto:")) {
    return str.substring(7);
  }

  // Return the string as-is (might be a name or email)
  return str;
}

/**
 * Parse organizer from ical event
 */
function parseOrganizer(organizerData: any): string {
  if (!organizerData) return "";

  // Handle object format
  if (typeof organizerData === "object") {
    if (organizerData.val) {
      return extractEmailFromString(organizerData.val);
    }
    if (organizerData.params && organizerData.params.CN) {
      return organizerData.params.CN;
    }
  }

  // Handle string format
  if (typeof organizerData === "string") {
    return extractEmailFromString(organizerData);
  }

  return "";
}

/**
 * Parse status from ical event
 */
function parseStatus(statusData: any): "confirmed" | "tentative" | "cancelled" {
  if (!statusData) return "confirmed";

  const status = String(statusData).toLowerCase();
  switch (status) {
    case "confirmed":
      return "confirmed";
    case "tentative":
      return "tentative";
    case "cancelled":
      return "cancelled";
    default:
      return "confirmed";
  }
}

/**
 * Format recurrence rule for display
 */
function formatRecurrenceRule(rrule: any): string {
  if (!rrule) return "";

  // ical library might return rrule as object or string
  if (typeof rrule === "string") {
    return rrule;
  }

  if (typeof rrule === "object") {
    // Convert object to RRULE string format
    const parts: string[] = [];

    Object.entries(rrule).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        parts.push(`${key.toUpperCase()}=${value}`);
      }
    });

    return parts.length > 0 ? `RRULE:${parts.join(";")}` : "";
  }

  return "";
}

/**
 * Validate that the content looks like an iCal file
 */
export function validateICalContent(content: string): boolean {
  const trimmedContent = content.trim();
  return (
    trimmedContent.includes("BEGIN:VCALENDAR") &&
    trimmedContent.includes("END:VCALENDAR") &&
    trimmedContent.includes("BEGIN:VEVENT")
  );
}

/**
 * Get basic info about an iCal file without full parsing
 */
export function getICalInfo(content: string): {
  isValid: boolean;
  eventCount: number;
  version?: string | undefined;
  prodId?: string | undefined;
} {
  try {
    const isValid = validateICalContent(content);
    const eventCount = (content.match(/BEGIN:VEVENT/g) || []).length;

    const versionMatch = content.match(/VERSION:([^\r\n]+)/);
    const prodIdMatch = content.match(/PRODID:([^\r\n]+)/);

    return {
      isValid,
      eventCount,
      version: versionMatch?.[1],
      prodId: prodIdMatch?.[1],
    };
  } catch (error) {
    return {
      isValid: false,
      eventCount: 0,
    };
  }
}

/**
 * Parse iCal file from File object
 */
export async function parseICalFromFile(file: File): Promise<ParsedEvent[]> {
  try {
    const content = await file.text();
    return parseICalFile(content);
  } catch (error) {
    throw new ICalParseError(
      `Failed to read or parse file "${file.name}": ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      error
    );
  }
}
